import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useQuizDetail } from '@/api/quiz';
import { type CheckMyQuizStatus, useCheckMyQuiz } from '@/hooks/useCheckMyQuiz';
import { getLightLayout } from '@/layout';
import { useUser } from '@/lib/auth';
import { DetailPageLayout, MobileCTAFooter } from '@/shared/components';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { useCreateAssignmentDialogEffect } from '@/shared/googleClassroom';
import { useCanCreateClassroom } from '@/shared/hooks';
import { useIsInLti } from '@/shared/lti';
import { LtiSelectQuizButton } from '@/shared/lti/components/LtiSelectQuizButton';
import { cn } from '@/utils/tailwind.util';
import { QuizRecommendationsSection } from './components/QuizRecommendationsSection';
import { SubHeader } from './components/SubHeader';
import { useQuizSavedLinkNudgeEffect } from './features/class-link-nudge/useQuizSavedLinkNudgeEffect';
import { EditButton } from './features/cta-buttons/components/Buttons';
import { DesktopCTAButtonGroup } from './features/cta-buttons/components/DesktopCTAButtonGroup';
import { LockedPlanFloatingText } from './features/cta-buttons/components/LockedPlanFloatingText';
import { MobileCTAButtonGroup } from './features/cta-buttons/components/MobileCTAButtonGroup';
import { WorksheetPromoCard } from './features/cta-buttons/components/WorksheetPromoCard';
import { useCTAScenario } from './features/cta-buttons/hooks/useCTAScenario';
import { useEditHandler } from './features/cta-buttons/hooks/useEditHandler';
import { useExportWorksheetHandler } from './features/cta-buttons/hooks/useExportWorksheetHandler';
import { useLinkToClassroomHandler } from './features/cta-buttons/hooks/useLinkToClassroomHandler';
import { QuizContentSection } from './features/quiz-content/components/QuizContentSection';
import { QuizDetailHeroCard } from './features/quiz-detail-hero-card/QuizDetailHeroCard';
import { useQuizDetailPageView } from './hooks/useQuizDetailPageView';
import { AnswerVisibilityProvider } from './stores/AnswerVisibilityProvider';
interface PageProps {
  quizId: string;
}
const PREVIEW_QUIZ_COUNT = 4;
QuizDetailPageView.getLayout = getLightLayout;
export function QuizDetailPageView(props: PageProps) {
  const quizId = props.quizId;
  const [isDetailCardInView, setIsDetailCardInView] = useState(true);
  const [isMainSectionInView, setIsMainSectionInView] = useState(true);
  const isSubHeaderVisible = !isDetailCardInView && isMainSectionInView;
  const {
    meStatus
  } = useCheckMyQuiz(quizId, true);
  const {
    isAuthenticated,
    isInitialized
  } = useUser();
  const {
    data: quizSetWithQuizList
  } = useQuizDetail({
    variables: {
      id: quizId
    }
  });

  // SSR/하이드레이션 이전(isInitialized=false)에는 로그인 가드를 렌더링하지 않는다.
  // 서버는 로그인 여부를 알 수 없어 가드/잘린 퀴즈 목록이 초기 HTML에 포함되면
  // SEO 크롤러가 미리보기만 보게 된다. 클라이언트 초기화 이후에만 가드를 노출한다.
  const shouldShowLoginGuard = isInitialized && !isAuthenticated && quizSetWithQuizList?.data?.quizList?.length !== undefined && quizSetWithQuizList?.data?.quizList?.length > 3;
  useCreateAssignmentDialogEffect({
    quizSet: quizSetWithQuizList?.data
  });
  const {
    scenario
  } = useCTAScenario({
    quizOwnerId: quizSetWithQuizList?.data?.authorId || '',
    isLocked: quizSetWithQuizList?.data?.isLocked || false
  });
  const canCreateClassroom = useCanCreateClassroom();
  useQuizSavedLinkNudgeEffect({
    quizSet: quizSetWithQuizList?.data,
    isOwner: scenario === 'owner'
  });
  const isQuizAuthor = scenario === 'owner' || scenario === 'owner-locked';
  useQuizDetailPageView({
    quizSetId: quizSetWithQuizList?.data?.id,
    isLocked: quizSetWithQuizList?.data?.isLocked ?? false,
    isOwner: isQuizAuthor
  });
  const handleEdit = useEditHandler({
    quizId,
    quizData: quizSetWithQuizList?.data ?? {} as QuizSetDetailsRes
  });
  const handleExportWorksheet = useExportWorksheetHandler({
    quizData: quizSetWithQuizList?.data ?? {} as QuizSetDetailsRes
  });
  const handleLinkToClassroom = useLinkToClassroomHandler({
    quizData: quizSetWithQuizList?.data ?? {} as QuizSetDetailsRes
  });
  const [ctaNode, setCtaNode] = useState<HTMLDivElement | null>(null);
  const [promoNode, setPromoNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!ctaNode || !promoNode) return;
    const PROMO_GAP = 0;
    let rafId = 0;
    let lastTop = -1;
    const apply = () => {
      rafId = 0;
      const top = ctaNode.getBoundingClientRect().bottom + PROMO_GAP;
      if (top === lastTop) return;
      lastTop = top;
      promoNode.style.top = `${top}px`;
    };
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(ctaNode);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [ctaNode, promoNode]);
  const isInExternalPlatform = useIsInExternalPlatform();
  const isInLti = useIsInLti();
  const displayQuizList = useMemo(() => {
    if (!quizSetWithQuizList) return [];
    return shouldShowLoginGuard ? quizSetWithQuizList.data.quizList.slice(0, PREVIEW_QUIZ_COUNT) : quizSetWithQuizList.data.quizList;
  }, [shouldShowLoginGuard, quizSetWithQuizList]);
  const {
    ref: heroCardRef
  } = useInView({
    onChange: inView => {
      setIsDetailCardInView(inView);
    },
    rootMargin: '-124px 0px 0px 0px'
  });
  const {
    ref: mainSectionRef
  } = useInView({
    threshold: 0,
    onChange: inView => {
      setIsMainSectionInView(inView);
    }
  });
  if (!quizSetWithQuizList) return;
  const isOwner = scenario === 'owner';
  const canLinkToClassroom = isOwner && canCreateClassroom;
  return <AnswerVisibilityProvider quizId={quizId} quizCount={quizSetWithQuizList.data.quizList.length} isQuizAuthor={isQuizAuthor} data-sentry-element="AnswerVisibilityProvider" data-sentry-component="QuizDetailPageView" data-sentry-source-file="QuizDetailPageView.tsx">
      <SubHeader thumbnailUrl={quizSetWithQuizList.data.templateImageUrl || ''} title={quizSetWithQuizList.data.title} authorName={quizSetWithQuizList.data.authorUsername} authorId={quizSetWithQuizList.data.authorId} isVisible={isSubHeaderVisible} isLocked={quizSetWithQuizList.data.isLocked} data-sentry-element="SubHeader" data-sentry-source-file="QuizDetailPageView.tsx" />

      <DetailPageLayout bannerUrl={quizSetWithQuizList.data.mapTemplateBannerImage} bannerAlt={quizSetWithQuizList.data.templateName} mainRef={mainSectionRef} containerClassName={cn(shouldShowLoginGuard && '!mb-[64px] !pb-sm mobile-lg:!pb-lg')} hero={<QuizDetailHeroCard ref={heroCardRef} quizData={quizSetWithQuizList.data} meStatus={meStatus} />} aside={isInExternalPlatform ? undefined : <>
              <div className="flex-1">
                <div ref={setCtaNode} className="sticky top-[140px] z-10">
                  <DesktopCTAButtonGroup scenario={scenario} quizId={quizId} quizData={quizSetWithQuizList.data} className="hidden tablet-sm:flex" />
                </div>
              </div>
              <div ref={setPromoNode} className="sticky bottom-2xl pt-xl">
                <WorksheetPromoCard onClick={handleExportWorksheet} />
              </div>
            </>} data-sentry-element="DetailPageLayout" data-sentry-source-file="QuizDetailPageView.tsx">
        <QuizContentSection quizSetWithQuizList={quizSetWithQuizList} displayQuizList={displayQuizList} shouldShowLoginGuard={shouldShowLoginGuard} onLinkToClassroom={canLinkToClassroom ? handleLinkToClassroom : undefined} data-sentry-element="QuizContentSection" data-sentry-source-file="QuizDetailPageView.tsx" />
      </DetailPageLayout>

      {!isInExternalPlatform && <QuizRecommendationsSection quizSetWithQuizList={quizSetWithQuizList} quizId={quizId} isMyQuiz={meStatus === 'isMe'} />}

      {/* 잠긴 퀴즈는 하단 CTA 위에 안내 띠가 더 붙는다. 페이지 끝 콘텐츠가 그 뒤에 가리지 않게 여백을 둔다. */}
      {!isInExternalPlatform && quizSetWithQuizList.data.isLocked && <div aria-hidden className="h-[104px] bg-neutral tablet-sm:hidden" />}

      {!isInExternalPlatform && <MobileCTAFooter floatingText={scenario === 'owner-locked' || scenario === 'viewer-locked' ? <LockedPlanFloatingText isOwner={scenario === 'owner-locked'} /> : undefined}>
          <MobileCTAButtonGroup scenario={scenario} quizId={quizId} quizData={quizSetWithQuizList.data} />
        </MobileCTAFooter>}
      {isInLti && quizSetWithQuizList && <LtiStickyFooter quizId={quizId} quizData={quizSetWithQuizList.data} meStatus={meStatus} onEdit={handleEdit} />}
    </AnswerVisibilityProvider>;
}
interface LtiStickyFooterProps {
  quizId: string;
  quizData: QuizSetDetailsRes;
  meStatus: CheckMyQuizStatus;
  onEdit: () => void;
}
const LtiStickyFooter = ({
  quizId,
  quizData,
  meStatus,
  onEdit
}: LtiStickyFooterProps) => {
  const canSelectQuizForLti = !quizData.isLocked;
  const isMyQuiz = meStatus === 'isMe';
  if (!canSelectQuizForLti && !isMyQuiz) return null;
  return <div className="sticky bottom-0 z-40 border-t border-alternative bg-white px-md py-sm" data-sentry-component="LtiStickyFooter" data-sentry-source-file="QuizDetailPageView.tsx">
      <div className="mx-auto flex max-w-[1024px] gap-xs">
        {isMyQuiz && <EditButton onClick={onEdit} className={cn('h-10 min-h-10 px-3 text-body-md-semibold', canSelectQuizForLti ? 'w-2/5' : 'w-full')} variant="outlined" intent="alternative" />}
        {canSelectQuizForLti && <div className={isMyQuiz ? 'w-3/5' : 'w-full'}>
            <LtiSelectQuizButton quizSetId={quizId} entryMapHashId={quizData.entryMapHashId} quizTitle={quizData.title} className="h-10 min-h-10" fullWidth />
          </div>}
      </div>
    </div>;
};